import React, { useState, useMemo, useCallback } from 'react';
import { Package, ChevronDown, Search, X, Check, ArrowRight, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { trackEvent } from '@/utils/analytics';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';
import { products } from '@/data/products';
import {
  sampleTypes,
  sampleFormats,
  sampleCategories,
  usStates
} from '@/data/sampleRequestData';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// ─── Sub-components ───────────────────────────────────────────

const SectionHeader = ({ number, title, done }) => (
  <div className="flex items-center gap-2.5 mb-3">
    <span className={cn(
      "flex items-center justify-center w-6 h-6 rounded-full text-xs font-semibold shrink-0",
      done ? "bg-[#2E7D32] text-white" : "bg-gray-200 text-gray-600"
    )}>
      {done ? <Check className="w-3.5 h-3.5" /> : number}
    </span>
    <h3 className="text-sm font-semibold text-gray-900 tracking-wide uppercase">{title}</h3>
  </div>
);

const ChipButton = ({ selected, onClick, children, className = "" }) => (
  <button
    type="button"
    onClick={onClick}
    className={cn(
      "px-3.5 py-2 rounded-lg border text-sm font-medium transition-all duration-150",
      selected
        ? "border-[#2E7D32] bg-[#2E7D32]/5 text-[#2E7D32] ring-1 ring-[#2E7D32]/20"
        : "border-gray-200 text-gray-700 hover:border-gray-300 hover:bg-gray-50",
      className
    )}
    data-testid={`chip-${children?.toString().toLowerCase().replace(/[^a-z0-9]/g, '-')}`}
  >
    {children}
  </button>
);

const ProductSearchSelect = ({ categoryId, selectedProducts, onToggle }) => {
  const [search, setSearch] = useState('');

  const categoryProducts = useMemo(() => {
    const list = products
      .filter(p => p.categoryId === categoryId)
      .map(p => ({ id: p.id, name: p.name, slug: p.slug }));

    // When multiple products in a category share the same display name
    // (e.g. "Nature View Style Rustique" exists in 4 sizes), append a size
    // suffix derived from the slug so users can distinguish them.
    const nameCounts = list.reduce((acc, p) => {
      acc[p.name] = (acc[p.name] || 0) + 1;
      return acc;
    }, {});
    const sizeFromSlug = (slug) => {
      // Match patterns like 6x36, 7x48, 18x18, 18x36
      const m = slug.match(/(\d{1,2})x(\d{1,2})/);
      if (!m) return '';
      return `${m[1]}" x ${m[2]}"`;
    };
    return list.map(p => {
      if (nameCounts[p.name] > 1) {
        const sz = sizeFromSlug(p.slug);
        return sz ? { ...p, name: `${p.name} — ${sz}` } : p;
      }
      return p;
    });
  }, [categoryId]);

  const filtered = useMemo(() => {
    if (!search.trim()) return categoryProducts;
    const q = search.toLowerCase();
    return categoryProducts.filter(p => p.name.toLowerCase().includes(q));
  }, [categoryProducts, search]);

  return (
    <div className="border rounded-lg overflow-hidden" data-testid="product-search-select">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        <input
          type="text"
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Search products..."
          className="w-full pl-9 pr-3 py-2.5 text-sm border-b border-gray-200 outline-none focus:ring-1 focus:ring-[#2E7D32]/30 bg-gray-50"
          data-testid="product-search-input"
        />
      </div>
      <div className="max-h-44 overflow-y-auto divide-y divide-gray-100">
        {filtered.length === 0 ? (
          <p className="px-3 py-4 text-sm text-gray-400 text-center">No products found</p>
        ) : (
          filtered.map(product => {
            const isSelected = selectedProducts.includes(product.slug);
            return (
              <label
                key={product.id}
                className={cn(
                  "flex items-center gap-3 px-3 py-2.5 cursor-pointer transition-colors text-sm",
                  isSelected ? "bg-[#2E7D32]/5" : "hover:bg-gray-50"
                )}
              >
                <span className={cn(
                  "flex items-center justify-center w-4.5 h-4.5 rounded border shrink-0 transition-all",
                  isSelected
                    ? "bg-[#2E7D32] border-[#2E7D32] text-white"
                    : "border-gray-300"
                )} style={{ width: 18, height: 18 }}>
                  {isSelected && <Check className="w-3 h-3" />}
                </span>
                <span className={cn("text-gray-700", isSelected && "font-medium text-gray-900")}>
                  {product.name}
                </span>
                <input
                  type="checkbox"
                  className="sr-only"
                  checked={isSelected}
                  onChange={() => onToggle(product.slug)}
                  data-testid={`product-checkbox-${product.slug}`}
                />
              </label>
            );
          })
        )}
      </div>
      {selectedProducts.length > 0 && (
        <div className="px-3 py-2 bg-gray-50 border-t text-xs text-gray-500">
          {selectedProducts.length} product{selectedProducts.length !== 1 ? 's' : ''} selected
        </div>
      )}
    </div>
  );
};

// ─── Main Modal ───────────────────────────────────────────────

const RequestSamplesModal = ({ isOpen, onClose }) => {
  // Contact / Shipping
  const [contact, setContact] = useState({
    fullName: '', email: '', company: '', phone: '',
    address: '', city: '', state: '', zip: ''
  });

  // Sample selection (progressive)
  const [sampleType, setSampleType] = useState('');
  const [sampleFormat, setSampleFormat] = useState('');
  const [category, setCategory] = useState('');
  const [selectedProducts, setSelectedProducts] = useState([]);

  // Request details
  const [colors, setColors] = useState('');
  const [quantity, setQuantity] = useState('1');
  const [notes, setNotes] = useState('');

  // UI state
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const updateContact = useCallback((field, value) => {
    setContact(prev => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors(prev => ({ ...prev, [field]: undefined }));
  }, [errors]);

  const toggleProduct = useCallback((slug) => {
    setSelectedProducts(prev =>
      prev.includes(slug) ? prev.filter(s => s !== slug) : [...prev, slug]
    );
  }, []);

  const handleSampleTypeChange = (id) => {
    setSampleType(id);
    setSampleFormat('');
    setCategory('');
    setSelectedProducts([]);
  };

  const handleCategoryChange = (id) => {
    setCategory(id);
    setSelectedProducts([]);
  };

  // Validation
  const validate = () => {
    const errs = {};
    if (!contact.fullName.trim()) errs.fullName = true;
    if (!contact.email.trim() || !/\S+@\S+\.\S+/.test(contact.email)) errs.email = true;
    if (!contact.address.trim()) errs.address = true;
    if (!contact.city.trim()) errs.city = true;
    if (!contact.state) errs.state = true;
    if (!contact.zip.trim()) errs.zip = true;
    if (!sampleType) errs.sampleType = true;
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const getProductNames = () => {
    return selectedProducts.map(slug => {
      const p = products.find(pr => pr.slug === slug);
      return p ? p.name : slug;
    });
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setIsSubmitting(true);

    const payload = {
      contact,
      sampleType: sampleTypes.find(s => s.id === sampleType)?.label || sampleType,
      sampleFormat: sampleFormats[sampleType]?.find(f => f.id === sampleFormat)?.label || sampleFormat || 'Not specified',
      productCategory: sampleCategories.find(c => c.id === category)?.label || category || 'Not specified',
      selectedProducts: getProductNames(),
      colors: colors.trim() || 'Not specified',
      quantity,
      notes: notes.trim() || 'None'
    };

    try {
      const res = await fetch(`/api/sample-requests`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      if (!res.ok) throw new Error('Submit failed');
      trackEvent('sample_requested', {
        form_type: 'sample_request',
        sample_type: sampleType || 'unspecified',
        category: category || 'unspecified',
        product_count: selectedProducts.length,
      });
      setIsSuccess(true);
    } catch {
      setErrors(prev => ({ ...prev, submit: "We couldn't submit your sample request. Please try again, or email samples@porticosystems.com." }));
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetAndClose = () => {
    setContact({ fullName: '', email: '', company: '', phone: '', address: '', city: '', state: '', zip: '' });
    setSampleType(''); setSampleFormat(''); setCategory('');
    setSelectedProducts([]); setColors(''); setQuantity('1'); setNotes('');
    setErrors({}); setIsSuccess(false); setIsSubmitting(false);
    onClose();
  };

  // Currently active formats based on selected sample type
  const activeFormats = sampleType ? (sampleFormats[sampleType] || []) : [];

  // ─── Render ─────────────────────────────────────────────

  if (isSuccess) {
    return (
      <Dialog open={isOpen} onOpenChange={resetAndClose}>
        <DialogContent className="max-w-md" data-testid="sample-request-success">
          <DialogHeader className="sr-only">
            <DialogTitle>Request Submitted</DialogTitle>
            <DialogDescription>Your sample request has been submitted successfully.</DialogDescription>
          </DialogHeader>
          <div className="flex flex-col items-center text-center py-8 px-4">
            <div className="w-14 h-14 rounded-full bg-[#2E7D32]/10 flex items-center justify-center mb-5">
              <Check className="w-7 h-7 text-[#2E7D32]" />
            </div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">Request Submitted</h2>
            <p className="text-gray-500 text-sm leading-relaxed max-w-xs">
              Thank you. Your sample request has been submitted successfully. Our Samples Department will review it and follow up shortly.
            </p>
            <Button onClick={resetAndClose} className="mt-6 bg-[#2E7D32] hover:bg-[#256428]" data-testid="success-close-btn">
              Done
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={isOpen} onOpenChange={resetAndClose}>
      <DialogContent
        className="max-w-2xl max-h-[92vh] flex flex-col p-0 gap-0 overflow-hidden"
        data-testid="request-samples-modal"
      >
        {/* Fixed Header */}
        <DialogHeader className="px-6 pt-5 pb-4 border-b shrink-0">
          <DialogTitle className="flex items-center gap-2.5 text-lg">
            <Package className="w-5 h-5 text-[#2E7D32]" />
            Request Samples
          </DialogTitle>
          <DialogDescription className="sr-only">
            Request product samples by filling out your contact, shipping, and sample selection details.
          </DialogDescription>
        </DialogHeader>

        {/* Scrollable Body */}
        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-6">

          {/* ── Section 1: Contact & Shipping ── */}
          <div>
            <SectionHeader number={1} title="Contact & Shipping" done={!!contact.fullName && !!contact.email && !!contact.address} />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="rs-name" className="text-xs text-gray-500">Full Name *</Label>
                <Input
                  id="rs-name" placeholder="Jane Smith"
                  value={contact.fullName}
                  onChange={e => updateContact('fullName', e.target.value)}
                  className={cn("h-9", errors.fullName && "border-red-400 ring-1 ring-red-200")}
                  data-testid="rs-fullname"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="rs-email" className="text-xs text-gray-500">Email *</Label>
                <Input
                  id="rs-email" type="email" placeholder="jane@company.com"
                  value={contact.email}
                  onChange={e => updateContact('email', e.target.value)}
                  className={cn("h-9", errors.email && "border-red-400 ring-1 ring-red-200")}
                  data-testid="rs-email"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="rs-company" className="text-xs text-gray-500">Company</Label>
                <Input
                  id="rs-company" placeholder="Company Name"
                  value={contact.company}
                  onChange={e => updateContact('company', e.target.value)}
                  className="h-9"
                  data-testid="rs-company"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="rs-phone" className="text-xs text-gray-500">Phone</Label>
                <Input
                  id="rs-phone" type="tel" placeholder="(555) 123-4567"
                  value={contact.phone}
                  onChange={e => updateContact('phone', e.target.value)}
                  className="h-9"
                  data-testid="rs-phone"
                />
              </div>
              <div className="space-y-1.5 sm:col-span-2">
                <Label htmlFor="rs-address" className="text-xs text-gray-500">Shipping Address *</Label>
                <Input
                  id="rs-address" placeholder="123 Main Street"
                  value={contact.address}
                  onChange={e => updateContact('address', e.target.value)}
                  className={cn("h-9", errors.address && "border-red-400 ring-1 ring-red-200")}
                  data-testid="rs-address"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="rs-city" className="text-xs text-gray-500">City *</Label>
                <Input
                  id="rs-city" placeholder="City"
                  value={contact.city}
                  onChange={e => updateContact('city', e.target.value)}
                  className={cn("h-9", errors.city && "border-red-400 ring-1 ring-red-200")}
                  data-testid="rs-city"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label htmlFor="rs-state" className="text-xs text-gray-500">State *</Label>
                  <div className="relative">
                    <select
                      id="rs-state"
                      value={contact.state}
                      onChange={e => updateContact('state', e.target.value)}
                      className={cn(
                        "w-full h-9 px-3 pr-8 rounded-md border text-sm bg-white appearance-none outline-none focus:ring-1 focus:ring-ring",
                        errors.state ? "border-red-400 ring-1 ring-red-200" : "border-input"
                      )}
                      data-testid="rs-state"
                    >
                      <option value="">Select</option>
                      {usStates.map(st => <option key={st} value={st}>{st}</option>)}
                    </select>
                    <ChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400 pointer-events-none" />
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="rs-zip" className="text-xs text-gray-500">ZIP Code *</Label>
                  <Input
                    id="rs-zip" placeholder="12345"
                    value={contact.zip}
                    onChange={e => updateContact('zip', e.target.value)}
                    className={cn("h-9", errors.zip && "border-red-400 ring-1 ring-red-200")}
                    data-testid="rs-zip"
                  />
                </div>
              </div>
            </div>
          </div>

          <hr className="border-gray-100" />

          {/* ── Section 2: Sample Selection ── */}
          <div>
            <SectionHeader number={2} title="Sample Selection" done={!!sampleType && !!sampleFormat} />

            {/* Step A: Sample Type */}
            <div className="mb-4">
              <p className="text-xs text-gray-500 mb-2">What type of samples do you need?</p>
              <div className="flex flex-wrap gap-2">
                {sampleTypes.map(type => (
                  <ChipButton
                    key={type.id}
                    selected={sampleType === type.id}
                    onClick={() => handleSampleTypeChange(type.id)}
                  >
                    {type.label}
                  </ChipButton>
                ))}
              </div>
              {errors.sampleType && !sampleType && (
                <p className="text-xs text-red-500 mt-1.5">Please select a sample type</p>
              )}
            </div>

            {/* Step B: Sample Format — revealed after type selected */}
            {sampleType && activeFormats.length > 0 && (
              <div className="mb-4 animate-in fade-in slide-in-from-top-2 duration-200">
                <p className="text-xs text-gray-500 mb-2">Sample size / format</p>
                <div className="flex flex-wrap gap-2">
                  {activeFormats.map(fmt => (
                    <ChipButton
                      key={fmt.id}
                      selected={sampleFormat === fmt.id}
                      onClick={() => setSampleFormat(fmt.id)}
                    >
                      {fmt.label}
                    </ChipButton>
                  ))}
                </div>
              </div>
            )}

            {/* Step C: Product Category — revealed after format */}
            {sampleType && sampleFormat && (
              <div className="mb-4 animate-in fade-in slide-in-from-top-2 duration-200">
                <p className="text-xs text-gray-500 mb-2">Product category</p>
                <div className="relative">
                  <select
                    value={category}
                    onChange={e => handleCategoryChange(e.target.value)}
                    className="w-full h-9 px-3 pr-8 rounded-md border border-input text-sm bg-white appearance-none outline-none focus:ring-1 focus:ring-ring"
                    data-testid="rs-category-select"
                  >
                    <option value="">Select a category...</option>
                    {sampleCategories.map(cat => (
                      <option key={cat.id} value={cat.id}>{cat.label}</option>
                    ))}
                  </select>
                  <ChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400 pointer-events-none" />
                </div>
              </div>
            )}

            {/* Step D: Product / Style Selection — revealed after category */}
            {category && (
              <div className="mb-4 animate-in fade-in slide-in-from-top-2 duration-200">
                <p className="text-xs text-gray-500 mb-2">
                  Select products / styles
                  <span className="text-gray-400 ml-1">(optional — select one or more)</span>
                </p>
                <ProductSearchSelect
                  categoryId={category}
                  selectedProducts={selectedProducts}
                  onToggle={toggleProduct}
                />
              </div>
            )}
          </div>

          {/* ── Section 3: Request Details — revealed after sample selection started ── */}
          {sampleType && (
            <>
              <hr className="border-gray-100" />
              <div className="animate-in fade-in slide-in-from-top-2 duration-200">
                <SectionHeader number={3} title="Request Details" done={false} />
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label htmlFor="rs-colors" className="text-xs text-gray-500">Colors Requested</Label>
                    <Input
                      id="rs-colors" placeholder='e.g. "Charcoal, Sand, Navy"'
                      value={colors}
                      onChange={e => setColors(e.target.value)}
                      className="h-9"
                      data-testid="rs-colors"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="rs-qty" className="text-xs text-gray-500">Quantity</Label>
                    <Input
                      id="rs-qty" type="number" min="1" max="99"
                      value={quantity}
                      onChange={e => setQuantity(e.target.value)}
                      className="h-9"
                      data-testid="rs-quantity"
                    />
                  </div>
                  <div className="space-y-1.5 sm:col-span-2">
                    <Label htmlFor="rs-notes" className="text-xs text-gray-500">Additional Notes / Special Instructions</Label>
                    <Textarea
                      id="rs-notes" rows={3}
                      placeholder="Any specific requirements, color matching needs, project details..."
                      value={notes}
                      onChange={e => setNotes(e.target.value)}
                      data-testid="rs-notes"
                    />
                  </div>
                </div>
              </div>
            </>
          )}
        </div>

        {/* Fixed Footer */}
        <div className="px-6 py-4 border-t bg-gray-50/80 flex flex-col gap-2 shrink-0">
          {errors.submit && (
            <p className="text-sm text-red-500 bg-red-50 p-3 rounded-lg" data-testid="rs-submit-error">{errors.submit}</p>
          )}
          <div className="flex items-center justify-end gap-3">
            <Button variant="outline" onClick={resetAndClose} data-testid="rs-cancel-btn">
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={isSubmitting}
              className="bg-[#2E7D32] hover:bg-[#256428] min-w-[140px]"
              data-testid="rs-submit-btn"
            >
              {isSubmitting ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Submitting...</>
              ) : (
                <><ArrowRight className="w-4 h-4 mr-2" /> Submit Request</>
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default RequestSamplesModal;
